import React from 'react';
import { Tab, TabList, TabPanel, Tabs } from 'react-tabs';

type TabData = {
  id: number;
  label: string;
  logo?: string;
  activeTab?: number;
  icon?: React.ComponentType<{ className?: string }>;
  badge?: number;
};

interface CustomTabsProps {
  activeTab: number;
  tabs: TabData[];
  onTabClick: (id: number, name: string) => void;
  /* eslint-disable react/require-default-props */
  children?: React.ReactNode;
  variant?: 'default' | 'pills';
  rightContent?: React.ReactNode;
  dataTour?: string;
}

function CustomTabs({
  activeTab,
  tabs,
  onTabClick,
  children,
  variant = 'default',
  rightContent,
  dataTour,
}: CustomTabsProps) {
  if (variant === 'pills') {
    return (
      <Tabs>
        <div className="flex flex-row items-center justify-between gap-6 mb-4 w-full">
          <TabList
            className="flex flex-wrap gap-1 rounded-lg bg-gray-100/50 p-1.5 h-auto w-max"
            key={activeTab}
            data-tour={dataTour}
          >
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <Tab
                  key={tab.id}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-md font-medium transition-all cursor-pointer outline-none focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 ${
                    isActive
                      ? 'bg-white shadow-md text-gray-900'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  onClick={() => onTabClick(tab.id, tab.label)}
                >
                  {Icon && <Icon className="w-4 h-4 flex-shrink-0" />}
                  {tab.logo && !Icon && <img src={tab.logo} alt="tab logo" className="w-4 h-4" />}
                  <span>{tab.label}</span>
                  {tab.badge != null && tab.badge > 0 ? (
                    <span className="ml-1 min-w-[1.25rem] text-center text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#E0F1F8] text-[#132961]">
                      {tab.badge}
                    </span>
                  ) : null}
                </Tab>
              );
            })}
          </TabList>
          {rightContent && <div className="flex-shrink-0 w-1/2">{rightContent}</div>}
        </div>
        {children &&
          tabs.map((tab) => (
            <TabPanel
              key={tab.id}
              className={`${activeTab === tab.id ? 'block' : 'hidden'} mt-0 pb-2 text-sm`}
            >
              {activeTab === tab.id && children ? children : null}
            </TabPanel>
          ))}
      </Tabs>
    );
  }

  return (
    <Tabs>
      <TabList className="flex flex-row" key={activeTab}>
        {tabs.map((tab) => (
          <Tab
            key={tab.id}
            className={`flex items-center px-7 pt-2 text-lg gap-2 hover:cursor-pointer font-bold ${
              activeTab === tab.id
                ? 'border-x-[1px] border-t-[1px] rounded-t-md bg-white border-b-0 pb-1'
                : 'border-b-[1px] pb-1'
            } outline-none focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0`}
            onClick={() => onTabClick(tab.id, tab.label)}
          >
            {tab.logo && <img src={tab.logo} alt="tab logo" />}
            <span className={`font-bold ${activeTab === tab.id ? 'text-black' : 'text-slate-400'}`}>
              {tab.label}
            </span>
          </Tab>
        ))}
      </TabList>

      {children &&
        tabs.map((tab) => (
          <TabPanel
            key={tab.id}
            className={`${activeTab === tab.id ? 'block' : 'hidden'} accordian pb-2 text-sm`}
          >
            {activeTab === tab.id && children ? children : null}
          </TabPanel>
        ))}
    </Tabs>
  );
}

export default CustomTabs;
